// src/components/Navbar.jsx
import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import gmcLogo from '../assets/gmc-logo-on-dark.png'

// Structured link configuration to keep routing centralized and DRY
const AUTH_LINKS = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/analyze', label: 'Analyse' },
]

const GUEST_LINKS = [
    { to: '/contact', label: 'Contact' },
    { to: '/login', label: 'Sign in' },
]

// Interface languages. English is the only selectable option for now; the rest
// are shown but disabled ("coming soon") — multi-language UI is an M5 deliverable.
const LANGUAGES = [
    { value: 'en', label: 'English', available: true },
    { value: 'es', label: 'Español (coming soon)', available: false },
    { value: 'pt', label: 'Português (coming soon)', available: false },
    { value: 'fr', label: 'Français (coming soon)', available: false },
    { value: 'it', label: 'Italiano (coming soon)', available: false },
    { value: 'de', label: 'Deutsch (coming soon)', available: false },
    { value: 'nl', label: 'Nederlands (coming soon)', available: false },
    { value: 'pl', label: 'Polski (coming soon)', available: false },
    { value: 'hr', label: 'Hrvatski (coming soon)', available: false },
    { value: 'ar', label: 'العربية (coming soon)', available: false },
    { value: 'ja', label: '日本語 (coming soon)', available: false },
    { value: 'zh', label: '中文 (coming soon)', available: false },
]

export default function Navbar() {
    const { user, logout } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()

    // Obscured admin route (env-driven, matches Profile.jsx). Only ever used
    // behind a user.is_superuser gate — the backend admin_required dependency
    // is the real access control; this path just keeps /admin off bot radar.
    const ADMIN_PATH = import.meta.env.VITE_ADMIN_PATH || 'gmc-secure-dashboard-7x9k2m'

    const [menuOpen, setMenuOpen] = useState(false)
    const [profileOpen, setProfileOpen] = useState(false)
    const profileRef = useRef(null)

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) setMenuOpen(false)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setProfileOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        setMenuOpen(false)
        setProfileOpen(false)
    }, [location])

    const handleLogout = async () => {
        try {
            await logout()
            setMenuOpen(false)
            setProfileOpen(false)
            navigate('/')
        } catch (error) {
            console.error('Logout error:', error)
        }
    }

    const handlePricingClick = (e) => {
        e.preventDefault()
        setMenuOpen(false)
        setProfileOpen(false)

        if (user) {
            navigate('/plans')
        } else {
            navigate('/login')
        }
    }

    return (
        <nav className="border-b border-white/[0.04] bg-[#07090e]/80 backdrop-blur-md sticky top-0 z-50 w-full select-none font-sans transition-all duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[5.5rem] md:h-[6.5rem] flex items-center justify-between">

                <Link to="/" className="flex items-center shrink-0 focus:outline-none" aria-label="GMC Home">
                    <img
                        src={gmcLogo}
                        alt="Guardian Message Check"
                        className="h-12 sm:h-14 md:h-16 lg:h-[4.5rem] w-auto object-contain"
                    />
                </Link>

                <div className="hidden md:flex items-center gap-6">
                    {user ? (
                        <>
                            {AUTH_LINKS.map((link) => {
                                const isActive = location.pathname === link.to
                                return (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        className={`text-sm font-medium tracking-wider uppercase transition-colors duration-200 ${isActive ? 'text-amber-400' : 'text-slate-500 hover:text-white'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                )
                            })}

                            {/* Pricing - redirects to /plans when logged in */}
                            <button
                                onClick={handlePricingClick}
                                className={`text-sm font-medium tracking-wider uppercase transition-colors duration-200 ${location.pathname === '/plans' ? 'text-amber-400' : 'text-slate-500 hover:text-white'
                                    }`}
                            >
                                Pricing
                            </button>

                            <div className="relative pl-4 border-l border-white/[0.08]" ref={profileRef}>
                                <button
                                    onClick={() => setProfileOpen(!profileOpen)}
                                    className="flex items-center gap-2 text-left focus:outline-none group"
                                    aria-haspopup="true"
                                    aria-expanded={profileOpen}
                                >
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-amber-500/20 to-indigo-500/20 border border-white/10 flex items-center justify-center text-xs font-bold text-amber-400 group-hover:border-amber-400/40 transition-colors">
                                        {user.email?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                    <span className="text-sm text-slate-400 group-hover:text-slate-200 transition-colors max-w-[120px] truncate hidden lg:inline">
                                        My Account
                                    </span>
                                </button>

                                {profileOpen && (
                                    <div className="absolute right-0 mt-3 w-56 bg-[#0c0e17] border border-white/[0.06] rounded-xl shadow-2xl p-2 transform origin-top-right transition-all duration-200 animate-in fade-in slide-in-from-top-2">
                                        <div className="px-3 py-2 border-b border-white/[0.04] mb-1">
                                            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">Account</p>
                                            <p className="text-sm font-mono text-slate-300 truncate mt-1">{user.email}</p>
                                        </div>
                                        <Link
                                            to="/profile"
                                            onClick={() => setProfileOpen(false)}
                                            className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/5 transition-colors block"
                                        >
                                            Profile Settings
                                        </Link>
                                        {user.is_superuser && (
                                            <button
                                                onClick={() => { setProfileOpen(false); navigate(`/${ADMIN_PATH}`) }}
                                                className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-brand-amber hover:bg-brand-amber/[0.08] transition-colors flex items-center gap-2"
                                            >
                                                Admin Panel
                                                <span className="ml-auto text-sm text-brand-amber/60">🔒</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-rose-400 hover:bg-rose-500/[0.06] transition-colors"
                                        >
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {GUEST_LINKS.map((link) => {
                                const isActive = location.pathname === link.to
                                return (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        className={`text-sm font-medium tracking-wider uppercase transition-colors duration-200 ${isActive ? 'text-amber-400' : 'text-slate-500 hover:text-white'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                )
                            })}

                            {/* Pricing - redirects to /login when not logged in */}
                            <button
                                onClick={handlePricingClick}
                                className={`text-sm font-medium tracking-wider uppercase transition-colors duration-200 ${location.pathname === '/login' ? 'text-amber-400' : 'text-slate-500 hover:text-white'
                                    }`}
                            >
                                Pricing
                            </button>

                            <Link to="/register"
                                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#07090e] px-5 py-2.5 rounded-lg text-sm font-bold tracking-wider uppercase transition-all duration-200 shadow-lg shadow-amber-500/5">
                                Get started
                            </Link>
                        </>
                    )}

                    <div className="pl-4 border-l border-white/[0.08]">
                        {/* Language dropdown. English is selectable and default; all
                            other languages are shown but disabled ("coming soon")
                            until the M5 multi-language release. Value is fixed to
                            'en' — this is presentational and drives no app state. */}
                        <select
                            value="en"
                            onChange={() => { }}
                            aria-label="Select language"
                            className="bg-transparent text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg px-2.5 py-2 focus:border-brand-amber/40 focus:outline-none cursor-pointer transition-colors"
                        >
                            {LANGUAGES.map((l) => (
                                <option
                                    key={l.value}
                                    value={l.value}
                                    disabled={!l.available}
                                    className="bg-[#0c0e17] text-slate-300"
                                >
                                    {l.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex md:hidden">
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        type="button"
                        className="text-slate-400 hover:text-white focus:outline-none p-2 rounded-lg transition-colors"
                        aria-label="Toggle Navigation"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            {menuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div className="md:hidden bg-[#07090e] border-b border-white/[0.04] px-4 pt-2 pb-6 space-y-1.5 animate-in fade-in slide-in-from-top-4 duration-300">
                    {user ? (
                        <>
                            <div className="p-3 bg-white/[0.01] border border-white/[0.04] rounded-xl mb-3">
                                <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">Account</p>
                                <p className="text-sm font-mono text-slate-300 truncate mt-1">{user.email}</p>
                            </div>

                            {AUTH_LINKS.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className="block text-sm font-light text-slate-300 hover:text-white py-2 px-1 transition-colors"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            ))}

                            {/* Mobile Pricing - redirects to /plans when logged in */}
                            <button
                                onClick={handlePricingClick}
                                className="block text-sm font-light text-slate-300 hover:text-white py-2 px-1 transition-colors w-full text-left"
                            >
                                Pricing
                            </button>

                            <Link
                                to="/profile"
                                className="block text-sm font-light text-slate-300 hover:text-white py-2 px-1 transition-colors"
                                onClick={() => setMenuOpen(false)}
                            >
                                Profile Settings
                            </Link>

                            {user.is_superuser && (
                                <button
                                    onClick={() => { setMenuOpen(false); navigate(`/${ADMIN_PATH}`) }}
                                    className="block w-full text-left text-sm font-light text-amber-400 hover:text-amber-300 py-2 px-1 transition-colors"
                                >
                                    Admin Panel 🔒
                                </button>
                            )}

                            <button
                                onClick={handleLogout}
                                className="w-full text-left text-rose-400 font-medium text-sm uppercase tracking-wider pt-3 mt-3 border-t border-white/[0.04]"
                            >
                                Sign Out
                            </button>
                        </>
                    ) : (
                        <>
                            {GUEST_LINKS.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className="block text-sm font-light text-slate-300 hover:text-white py-2 px-1 transition-colors"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            ))}

                            {/* Mobile Pricing - redirects to /login when not logged in */}
                            <button
                                onClick={handlePricingClick}
                                className="block text-sm font-light text-slate-300 hover:text-white py-2 px-1 transition-colors w-full text-left"
                            >
                                Pricing
                            </button>

                            <Link
                                to="/register"
                                className="block bg-gradient-to-r from-amber-500 to-amber-600 text-[#07090e] text-sm font-bold text-center py-3 rounded-xl uppercase tracking-wider mt-3 shadow-md"
                                onClick={() => setMenuOpen(false)}
                            >
                                Get started
                            </Link>
                        </>
                    )}

                    <div className="pt-4 mt-3 border-t border-white/[0.04]">
                        <p className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-2">Language</p>
                        {/* Language dropdown — English selectable/default, rest disabled. */}
                        <select
                            value="en"
                            onChange={() => { }}
                            aria-label="Select language"
                            className="w-full bg-white/[0.02] text-sm text-slate-300 border border-white/10 rounded-lg px-3 py-2.5 focus:border-brand-amber/40 focus:outline-none cursor-pointer"
                        >
                            {LANGUAGES.map((l) => (
                                <option
                                    key={l.value}
                                    value={l.value}
                                    disabled={!l.available}
                                    className="bg-[#0c0e17] text-slate-300"
                                >
                                    {l.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </nav>
    )
}